import { T, useT } from '@transifex/react';
import { Avatar, AvatarImage, Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import { updateUserHealthGoals } from '@/modules/api';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import {
  CtaButton,
  Screen,
  ScreenHeading,
  SelectableRow,
  SelectableRowSkeleton,
} from '@/modules/ui';

import { useHealthGoals } from './hooks/useHealthGoals';

/**
 * "What is your main health goal?" — the second static-survey step, reached
 * from health-conditions. Single-select (mirrors the vida-design-system
 * onboarding HealthGoals demo: avatar + name rows, selection by border
 * highlight). Persisted to Sparta's `urns: string[]` endpoint as a one-element
 * array.
 *
 * Part of the bi-directional STATIC SURVEY group, so the header shows a back
 * chevron returning to /step/health-conditions. It's the last built step:
 * goToNextStep re-derives to /step/health-goals (a no-op) until the next
 * group — program matching / dynamic screeners — exists.
 */
export function HealthGoalsScreen() {
  const t = useT();
  const { user } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();
  const { goals, initialSelection, isLoading, error } = useHealthGoals();

  // Local selection state (no form library — a single goal urn).
  const [selectedUrn, setSelectedUrn] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Seed the selection from the user's saved goal once it loads. The endpoint
  // is multi-capable (`urns`), but this screen is single-select — take the first.
  useEffect(() => {
    setSelectedUrn(initialSelection[0] ?? null);
  }, [initialSelection]);

  const onContinue = async () => {
    trackEvent('health-goals-click-continue', { uuid: user?.uuid });
    setSubmitError(false);

    // Only persist when the selection changed from what loaded — an untouched
    // form just advances, no redundant write.
    const initialUrn = initialSelection[0] ?? null;
    if (selectedUrn === initialUrn) {
      goToNextStep({ from: '/step/health-goals' });
      return;
    }

    const userUrn = user?.urn;
    if (!userUrn) {
      setSubmitError(true);
      return;
    }
    try {
      setIsSubmitting(true);
      await updateUserHealthGoals(userUrn, selectedUrn ? [selectedUrn] : []);
      goToNextStep({ from: '/step/health-goals' });
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting || isLoading}
          onPress={onContinue}
          testID='health-goals-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('What is your main health goal?')}
          subtitle={t('Choose a goal from the options below.')}
        />

        {error != null ? (
          <Text size='sm' className='text-error-700'>
            <T _str='We couldn’t load the list of goals. Please refresh and try again.' />
          </Text>
        ) : null}

        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong saving your goals. Please try again.' />
          </Text>
        ) : null}

        {isLoading ? (
          <SelectableRowSkeleton variant='avatar_and_text' count={5} rowHeight={96} />
        ) : (
          <VStack space='lg'>
            {goals.map((goal) => (
              <SelectableRow
                key={goal.urn}
                isSelected={selectedUrn === goal.urn}
                selectionRole='radio'
                onPress={() => setSelectedUrn(goal.urn)}
                accessibilityLabel={goal.name}
                testID={`health-goals-${goal.key}`}
              >
                <Avatar size='lg' className='bg-[#FDF6EE]'>
                  {goal.icon ? <AvatarImage source={{ uri: goal.icon }} alt={goal.name} /> : null}
                </Avatar>
                <Text size='md' weight='semibold' className='flex-1 text-typography-900'>
                  {goal.name}
                </Text>
              </SelectableRow>
            ))}
          </VStack>
        )}
      </VStack>
    </Screen>
  );
}
